import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight, Check, X, ShieldCheck, Sparkles, FileText, Calculator, TrendingUp,
  Layers, Lock, Building2, Search, Target, Rocket, LineChart, ChevronDown, Menu,
  Users, Bell, Briefcase, FolderOpen, BadgeCheck, Network,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import CaplyMark from '../components/CaplyMark';

/**
 * Caply marketing home page — public, no auth required.
 *
 * Written for a private equity deal team, not for a software buyer: plain
 * language about what the product does on a live deal, honest framing about
 * what it does not do, and pricing that a 5 to 50 person fund can read in
 * thirty seconds. Competitor figures are public estimates, labelled as such.
 */


const LIFECYCLE = [
  { icon: Search, name: 'Find', copy: 'Score targets against your thesis, then draft outreach that waits for your approval.' },
  { icon: FolderOpen, name: 'Analyze', copy: 'Upload or forward a CIM. Caply reads every file and writes the screening memo.' },
  { icon: Calculator, name: 'Offer', copy: 'Request lists, workstreams, gates, IOI, LOI, IC memo, term sheet, LBO and operating model.' },
  { icon: TrendingUp, name: 'Grow', copy: 'KPI cards per company, decline alerts, board decks and email digests.' },
  { icon: LineChart, name: 'Exit', copy: 'A clean record of what the deal earned and why, ready for the next buyer.' },
];

const CAPABILITIES = [
  {
    icon: FolderOpen,
    title: 'Deal intake that reads the documents',
    copy: 'Drop a CIM, a request list or a folder of financials. Caply extracts the numbers, files everything, and writes a screening memo that only cites what the file actually says.',
  },
  {
    icon: Target,
    title: 'Thesis fit, with the gaps named',
    copy: 'Your sectors, size band and margin floor become a score. Where the data does not answer a criterion, the deal is marked not reported instead of guessed.',
  },
  {
    icon: Briefcase,
    title: 'One page per deal',
    copy: 'Deal Review gathers the snapshot, what has arrived, what is still outstanding, who owns each item, and the whole-deal report.',
  },
  {
    icon: Layers,
    title: 'Diligence request tracking',
    copy: 'Import the counterparty numbered request list, track requested, received, reviewed and flagged, and link data-room files straight to the item they answer.',
  },
  {
    icon: FileText,
    title: 'Diligence analysis you can read',
    copy: 'Financials, billing, HR and CRM files are analysed file by file, with a plain-English digest of each one and the working capital detail behind it.',
  },
  {
    icon: Calculator,
    title: 'Modeling that shows its work',
    copy: 'LBO with sensitivity grids and Analysis at Various Prices, plus an operating model that builds EBITDA from organic growth, add-ons and new sites.',
  },
  {
    icon: BadgeCheck,
    title: 'Documents and phase gates',
    copy: 'IOI, LOI, IC memo, term sheet, cover letter and board pack drafted from the deal record, with gates that require a written reason to move.',
  },
  {
    icon: Bell,
    title: 'Portfolio monitoring',
    copy: 'Six KPI cards per company, honest decline alerts when a metric breaks its threshold, and board decks built from reported data only.',
  },
];

const COMPARE = [
  {
    row: 'Typical annual cost',
    caply: '$3,000 to $10,200 per user',
    crm: 'Affinity $2,000 to $2,700 per user. DealCloud is estimated at $15,000 to $40,000+ per user.',
    data: 'PitchBook $12,000 to $20,000 per seat. Grata from about $15,000 per firm.',
    monitoring: '$25,000 to $150,000 per firm per year.',
    vdr: '$10,000 to $50,000+ per deal project.',
  },
  {
    row: 'Screening and memos',
    caply: true,
    crm: 'Light, usually fields and notes.',
    data: 'Company data only, no memo.',
    monitoring: false,
    vdr: false,
  },
  {
    row: 'Diligence tracking and analysis',
    caply: true,
    crm: false,
    data: false,
    monitoring: 'Reported metrics only.',
    vdr: 'Document hosting and Q&A.',
  },
  {
    row: 'LBO and operating model',
    caply: true,
    crm: false,
    data: false,
    monitoring: false,
    vdr: false,
  },
  {
    row: 'Documents and IC packs',
    caply: true,
    crm: false,
    data: false,
    monitoring: 'Reporting packs.',
    vdr: false,
  },
  {
    row: 'Portfolio KPIs and alerts',
    caply: true,
    crm: false,
    data: false,
    monitoring: true,
    vdr: false,
  },
  {
    row: 'Paid market data universe',
    caply: 'No. Company search uses open data and your own lists.',
    crm: false,
    data: true,
    monitoring: false,
    vdr: false,
  },
];

const TIERS = [
  {
    slug: 'screening',
    name: 'Screening',
    yearly: 250,
    monthly: 300,
    seats: 2,
    for: 'A fund triaging far more deals than it can read.',
    features: [
      'Up to 15 companies reviewed a month',
      'CIM and email intake with screening memos',
      'Thesis scoring with named gaps',
      'KPI dashboards and Ask Caply',
      'Financial standardization from CSV',
      'Email support',
    ],
  },
  {
    slug: 'deal_team',
    name: 'Deal Team',
    yearly: 500,
    monthly: 600,
    seats: 3,
    for: 'The full deal lifecycle, from scan to signed IOI.',
    popular: true,
    features: [
      'Everything in Screening, unlimited deals',
      'Deal Review with request lists and workstreams',
      'Phase gates with rationale and history',
      'IOI, LOI, IC memo, term sheet, board pack',
      'LBO model and operating model',
      'Template library with your firm branding',
      'Priority support',
    ],
  },
  {
    slug: 'firm',
    name: 'Firm',
    yearly: 850,
    monthly: 1020,
    seats: 8,
    for: 'Funds running the portfolio as closely as the pipeline.',
    features: [
      'Everything in Deal Team',
      'Portfolio KPI cards and decline alerts',
      'Board decks and scheduled email digests',
      'Multi-fund workspaces',
      'SSO, audit log and role controls',
      'Named onboarding and quarterly review',
    ],
  },
];

const FAQ = [
  {
    q: 'Does Caply make numbers up?',
    a: 'No. Every figure traces back to a file you uploaded or a field you typed. When something is missing, the output says not reported and lists the gap rather than filling it in. That rule is enforced in the model, not just in the copy.',
  },
  {
    q: 'Where does deal data come from?',
    a: 'Your uploads, a connected Gmail or Google Drive, data-room links in an email, and the deal details your team enters. Caply does not buy or scrape private company financials.',
  },
  {
    q: 'Is this a PitchBook replacement?',
    a: 'No, and it is not priced like one. PitchBook and Grata answer which companies exist. Caply answers what to do with the companies already in front of you: screen them, diligence them, model them, and report on them.',
  },
  {
    q: 'How long does setup take?',
    a: 'A working afternoon. Connect Google if you want email and Drive intake, set your investment thesis, invite the team, and start with one live deal. There is no implementation project and no data migration.',
  },
  {
    q: 'What happens to our data?',
    a: 'Each firm gets an isolated workspace on Azure, with sign-in through Microsoft Entra and data encrypted at rest. Nothing sensitive is stored on a laptop or in a local file share, and every record carries who created it.',
  },
  {
    q: 'Can we try it on a real deal first?',
    a: 'Yes. The trial runs on your own documents, not a demo dataset, so you can compare the screening memo and the IC pack against what your team would have written.',
  },
];

function Mark({ className = 'w-9 h-9' }) {
  // never smaller than 32px: the arrow and gold arc need the room
  return <CaplyMark className={`${className} shrink-0`} title="Caply" />;
}

function Section({ id, children, className = '' }) {
  return (
    <section id={id} className={`scroll-mt-24 ${className}`}>
      {children}
    </section>
  );
}

function Eyebrow({ children }) {
  return (
    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-600">{children}</p>
  );
}

function HeroPanel() {
  const rows = [
    { name: 'NovaPath Robotics', sector: 'Industrial automation', phase: 'IOI issued', tone: 'bg-brand-100 text-brand-800' },
    { name: 'FinTest Crop 2', sector: 'Ag services', phase: 'Due diligence', tone: 'bg-navy-100 text-navy-800' },
    { name: 'Harbor Dental Group', sector: 'Dental platforms', phase: 'Screening', tone: 'bg-slate-100 text-slate-600' },
  ];
  return (
    <div className="relative">
      <div className="absolute -inset-4 bg-gradient-to-tr from-brand-100/70 via-white to-navy-50 rounded-3xl blur-xl" aria-hidden="true" />
      <div className="relative rounded-2xl border border-slate-200 bg-white shadow-cardHover overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100 bg-slate-50">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-slate-200" />
            <span className="w-2.5 h-2.5 rounded-full bg-slate-200" />
            <span className="w-2.5 h-2.5 rounded-full bg-slate-200" />
          </div>
          <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400">Deal pipeline</span>
        </div>

        <div className="p-4 space-y-3">
          {rows.map(r => (
            <div key={r.name} className="flex items-center justify-between rounded-lg border border-slate-100 bg-white px-3 py-2.5">
              <div>
                <p className="text-sm font-semibold text-navy-900">{r.name}</p>
                <p className="text-[11px] text-slate-400">{r.sector}</p>
              </div>
              <span className={`text-[10px] font-semibold rounded px-2 py-0.5 ${r.tone}`}>{r.phase}</span>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-3 gap-px bg-slate-100 border-t border-slate-100">
          {[
            { k: 'IRR', v: '24.8%' },
            { k: 'MOIC', v: '2.4x' },
            { k: 'Alerts', v: '3 open' },
          ].map(t => (
            <div key={t.k} className="bg-white px-3 py-3 text-center">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{t.k}</p>
              <p className="text-sm font-semibold text-navy-900">{t.v}</p>
            </div>
          ))}
        </div>
      </div>
      <p className="mt-2 text-right text-[10px] text-slate-400">Sample data</p>
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [menu, setMenu] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  const start = () => navigate(token ? '/portfolio' : `/signup?plan=deal_team&cycle=${cycle}`);
  const [cycle, setCycle] = useState('annual');
  const startWithPlan = (slug) => navigate(token ? '/portfolio' : `/signup?plan=${slug}&cycle=${cycle}`);
  const startLabel = token ? 'Open Caply' : 'Start a trial';

  const nav = [
    { label: 'Platform', href: '#platform' },
    { label: 'How it works', href: '#how' },
    { label: 'Pricing', href: '#pricing' },
    { label: 'Security', href: '#security' },
    { label: 'FAQ', href: '#faq' },
  ];

  return (
    <div className="min-h-screen bg-white text-slate-700">
      {/* ── nav ─────────────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-40 border-b border-slate-200/80 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3">
          <a href="#top" className="flex items-center gap-2.5">
            <Mark className="w-8 h-8" />
            <span className="text-[17px] font-semibold tracking-tight text-navy-900">Caply</span>
          </a>

          <nav className="hidden items-center gap-7 md:flex">
            {nav.map(n => (
              <a key={n.label} href={n.href} className="text-[13px] font-medium text-slate-500 transition-colors hover:text-navy-900">
                {n.label}
              </a>
            ))}
          </nav>

          <div className="hidden items-center gap-3 md:flex">
            <Link to="/login" className="text-[13px] font-medium text-slate-600 transition-colors hover:text-navy-900">
              Sign in
            </Link>
            <button onClick={start} className="inline-flex items-center gap-1.5 rounded-md bg-navy-900 px-3.5 py-2 text-[13px] font-semibold text-brand-100 transition-colors hover:bg-navy-800">
              {startLabel}<ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>

          <button onClick={() => setMenu(m => !m)} className="md:hidden rounded-md border border-slate-200 p-2" aria-label="Menu">
            <Menu className="w-4 h-4 text-navy-900" />
          </button>
        </div>

        {menu && (
          <div className="md:hidden border-t border-slate-100 bg-white px-5 py-3 space-y-2">
            {nav.map(n => (
              <a key={n.label} href={n.href} onClick={() => setMenu(false)} className="block py-1.5 text-sm font-medium text-slate-600">
                {n.label}
              </a>
            ))}
            <div className="flex items-center gap-2 pt-2">
              <Link to="/login" className="flex-1 rounded-md border border-slate-200 px-3 py-2 text-center text-sm font-semibold text-slate-700">Sign in</Link>
              <button onClick={start} className="flex-1 rounded-md bg-navy-900 px-3 py-2 text-sm font-semibold text-brand-100">{startLabel}</button>
            </div>
          </div>
        )}
      </header>

      {/* ── hero ────────────────────────────────────────────────────────── */}
      <div id="top">
        <Section className="mx-auto max-w-6xl px-5 pt-14 pb-16 md:pt-20">
          <div className="grid items-center gap-12 md:grid-cols-[1.05fr_0.95fr]">
            <div>
              <Eyebrow>Built for private equity deal teams</Eyebrow>
              <h1 className="mt-3 text-4xl font-semibold leading-[1.08] tracking-tight text-navy-900 md:text-[52px]">
                Every deal, from first look to final model.
              </h1>
              <p className="mt-5 max-w-xl text-[15px] leading-relaxed text-slate-600">
                Caply is one workspace for screening, diligence, deal documents and portfolio monitoring.
                Your analysts stop rebuilding the same spreadsheet, and your partners get numbers that
                trace back to the file they came from.
              </p>

              <div className="mt-7 flex flex-wrap items-center gap-3">
                <button onClick={start} className="inline-flex items-center gap-2 rounded-md bg-navy-900 px-5 py-3 text-sm font-semibold text-brand-100 transition-colors hover:bg-navy-800">
                  {startLabel}<ArrowRight className="w-4 h-4" />
                </button>
                <a href="#how" className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-5 py-3 text-sm font-semibold text-navy-900 transition-colors hover:bg-slate-50">
                  See the workflow
                </a>
              </div>

              <p className="mt-4 text-[12px] text-slate-400">
                Trial on your own documents. No implementation project, no data migration.
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-[12px] text-slate-500">
                <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-brand-600" />Isolated workspace per firm</span>
                <span className="inline-flex items-center gap-1.5"><Lock className="w-4 h-4 text-brand-600" />Microsoft Entra sign-in</span>
                <span className="inline-flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-brand-600" />No invented numbers</span>
              </div>
            </div>

            <HeroPanel />
          </div>
        </Section>
      </div>

      {/* ── lifecycle ───────────────────────────────────────────────────── */}
      <Section id="how" className="border-y border-slate-100 bg-slate-50/70">
        <div className="mx-auto max-w-6xl px-5 py-16">
          <div className="max-w-2xl">
            <Eyebrow>The deal lifecycle</Eyebrow>
            <h2 className="mt-2 text-3xl font-semibold tracking-tight text-navy-900">
              Five stages your team already uses. One place to run them.
            </h2>
            <p className="mt-3 text-[15px] text-slate-600">
              No new methodology to adopt. Caply maps onto the way a fund already works a deal,
              and keeps the record as the deal moves.
            </p>
          </div>

          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {LIFECYCLE.map((s, i) => (
              <div key={s.name} className="rounded-xl border border-slate-200 bg-white p-5">
                <div className="flex items-center justify-between">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-brand-50 text-brand-700">
                    <s.icon className="w-4 h-4" />
                  </span>
                  <span className="text-[11px] font-semibold text-slate-300">0{i + 1}</span>
                </div>
                <h3 className="mt-3 text-sm font-semibold text-navy-900">{s.name}</h3>
                <p className="mt-1.5 text-[12.5px] leading-relaxed text-slate-500">{s.copy}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ── capabilities ────────────────────────────────────────────────── */}
      <Section id="platform" className="mx-auto max-w-6xl px-5 py-16">
        <div className="max-w-2xl">
          <Eyebrow>The platform</Eyebrow>
          <h2 className="mt-2 text-3xl font-semibold tracking-tight text-navy-900">
            What your team actually gets on day one
          </h2>
          <p className="mt-3 text-[15px] text-slate-600">
            Eight pieces of work that currently live in spreadsheets, inboxes, and one analyst's memory.
          </p>
        </div>

        <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {CAPABILITIES.map(c => (
            <div key={c.title} className="rounded-xl border border-slate-200 bg-white p-5 transition-shadow hover:shadow-card">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-navy-900 text-brand-100">
                <c.icon className="w-4 h-4" />
              </span>
              <h3 className="mt-3.5 text-[13.5px] font-semibold leading-snug text-navy-900">{c.title}</h3>
              <p className="mt-2 text-[12.5px] leading-relaxed text-slate-500">{c.copy}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ── honest by design ────────────────────────────────────────────── */}
      <Section className="border-y border-slate-100 bg-navy-950">
        <div className="mx-auto max-w-6xl px-5 py-16">
          <div className="max-w-2xl">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-400">Why teams trust it</p>
            <h2 className="mt-2 text-3xl font-semibold tracking-tight text-white">
              The model says no before you say yes.
            </h2>
            <p className="mt-3 text-[15px] leading-relaxed text-slate-300">
              Most AI tools fail a deal team in one specific way: they fill in a blank with a plausible number.
              Caply is built the other way around.
            </p>
          </div>

          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {[
              { icon: Network, t: 'Every number traces back', c: 'Each figure in a memo, model or board pack comes from a file you supplied or a field you typed, and the source file travels with it.' },
              { icon: BadgeCheck, t: 'Gaps stay gaps', c: 'A missing margin, an unstated multiple, a criterion the CIM never addresses: it is reported as not reported, and listed as a gap, never estimated.' },
              { icon: Users, t: 'Drafts, not decisions', c: 'Caply prepares the memo, the model and the pack. Your partners still own the investment committee call, and the record shows who made it.' },
            ].map(x => (
              <div key={x.t} className="rounded-xl border border-white/10 bg-white/5 p-5">
                <x.icon className="w-5 h-5 text-brand-400" />
                <h3 className="mt-3 text-sm font-semibold text-white">{x.t}</h3>
                <p className="mt-2 text-[12.5px] leading-relaxed text-slate-300">{x.c}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ── pricing ─────────────────────────────────────────────────────── */}
      <Section id="pricing" className="mx-auto max-w-6xl px-5 py-16">
        <div className="max-w-2xl">
          <Eyebrow>Pricing</Eyebrow>
          <h2 className="mt-2 text-3xl font-semibold tracking-tight text-navy-900">
            Priced for a fund, not for a bank
          </h2>
          <p className="mt-3 text-[15px] text-slate-600">
            Per seat, all modules included, no per-deal data-room fees. Pay for the year or month by
            month: monthly billing costs 20% more over twelve months. Most funds start on Deal Team
            and add Firm when they want the portfolio on the same screen.
          </p>
        </div>

        <div className="mt-7 inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1" role="tablist" aria-label="Billing cycle">
          {[
            { id: 'annual', label: 'Yearly', hint: 'save 20%' },
            { id: 'monthly', label: 'Monthly', hint: 'cancel anytime' },
          ].map(opt => (
            <button
              key={opt.id}
              role="tab"
              aria-selected={cycle === opt.id}
              onClick={() => setCycle(opt.id)}
              className={`rounded-md px-4 py-2 text-[13px] font-semibold transition-colors ${cycle === opt.id ? 'bg-white text-navy-900 shadow-sm' : 'text-slate-500 hover:text-navy-900'}`}
            >
              {opt.label}
              <span className={`ml-1.5 text-[11px] font-medium ${opt.id === 'annual' ? 'text-brand-700' : 'text-slate-400'}`}>{opt.hint}</span>
            </button>
          ))}
        </div>

        <div className="mt-6 grid gap-5 lg:grid-cols-3">
          {TIERS.map(t => (
            <div key={t.name} className={`relative rounded-2xl border p-6 ${t.popular ? 'border-brand-400 bg-white shadow-cardHover' : 'border-slate-200 bg-white'}`}>
              {t.popular && (
                <span className="absolute -top-3 left-6 rounded-full bg-brand-500 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white">
                  Most funds start here
                </span>
              )}
              <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-400">{t.name}</h3>
              <div className="mt-3 flex items-end gap-1.5">
                <span className="text-3xl font-semibold tracking-tight text-navy-900">
                  ${(cycle === 'monthly' ? t.monthly : t.yearly).toLocaleString('en-US')}
                </span>
                <span className="pb-1 text-[12px] text-slate-500">per user / month</span>
              </div>
              <p className="mt-1 text-[11px] text-slate-400">
                {cycle === 'monthly'
                  ? `Billed monthly. Minimum ${t.seats} seats.`
                  : `Billed yearly. Minimum ${t.seats} seats.`}
              </p>
              <p className="text-[11px] text-slate-400">
                {cycle === 'monthly'
                  ? `${t.seats} seats: $${(t.monthly * t.seats).toLocaleString('en-US')} a month.`
                  : `${t.seats} seats: $${(t.yearly * t.seats * 12).toLocaleString('en-US')} a year ($${(t.yearly * t.seats).toLocaleString('en-US')} a month equivalent).`}
              </p>
              <p className="mt-3 text-[12.5px] font-medium text-slate-600">{t.for}</p>

              <button
                onClick={() => startWithPlan(t.slug)}
                className={`mt-5 w-full rounded-md px-4 py-2.5 text-[13px] font-semibold transition-colors ${t.popular ? 'bg-navy-900 text-brand-100 hover:bg-navy-800' : 'border border-slate-300 text-navy-900 hover:bg-slate-50'}`}
              >
                {token ? 'Open Caply' : `Start with ${t.name}`}
              </button>

              <ul className="mt-5 space-y-2.5">
                {t.features.map(f => (
                  <li key={f} className="flex gap-2 text-[12.5px] leading-snug text-slate-600">
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-600" />{f}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="text-sm font-semibold text-navy-900">Enterprise and multi-fund groups</h3>
            <p className="mt-1 text-[12.5px] text-slate-600">
              Dedicated tenant, private networking, custom model routing, security review and a signed DPA.
            </p>
          </div>
          <a href="mailto:support@caply4pe.com?subject=Caply%20enterprise" className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-navy-900 px-4 py-2.5 text-[13px] font-semibold text-navy-900 hover:bg-white">
            Talk to us<ArrowRight className="w-3.5 h-3.5" />
          </a>
        </div>

        {/* comparison */}
        <div className="mt-14">
          <h3 className="text-xl font-semibold tracking-tight text-navy-900">How that compares with the usual stack</h3>
          <p className="mt-2 max-w-3xl text-[13px] text-slate-500">
            Ranges below are public estimates gathered from vendor pages and 2026 buyer guides, not quotes.
            Every row is a category a mid-market fund typically pays for separately.
          </p>

          <div className="mt-5 overflow-x-auto rounded-xl border border-slate-200">
            <table className="w-full min-w-[900px] text-left text-[12px]">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="px-4 py-3 font-semibold"></th>
                  <th className="px-4 py-3 font-semibold text-navy-900">Caply</th>
                  <th className="px-4 py-3 font-semibold">Deal CRM<br /><span className="font-normal text-slate-400">Affinity, DealCloud</span></th>
                  <th className="px-4 py-3 font-semibold">Market data<br /><span className="font-normal text-slate-400">PitchBook, Grata</span></th>
                  <th className="px-4 py-3 font-semibold">Portfolio monitoring<br /><span className="font-normal text-slate-400">iLevel, Chronograph</span></th>
                  <th className="px-4 py-3 font-semibold">Data room<br /><span className="font-normal text-slate-400">Datasite, Ansarada</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {COMPARE.map((r, i) => (
                  <tr key={r.row} className={i === 0 ? 'bg-brand-50/50' : ''}>
                    <td className="px-4 py-3 font-medium text-slate-700">{r.row}</td>
                    <td className="px-4 py-3 font-medium text-navy-900">
                      {typeof r.caply === 'boolean'
                        ? (r.caply ? <Check className="h-4 w-4 text-brand-600" /> : <X className="h-4 w-4 text-slate-300" />)
                        : r.caply}
                    </td>
                    {[r.crm, r.data, r.monitoring, r.vdr].map((v, j) => (
                      <td key={j} className="px-4 py-3 text-slate-500">
                        {typeof v === 'boolean'
                          ? (v ? <Check className="h-4 w-4 text-slate-400" /> : <X className="h-4 w-4 text-slate-300" />)
                          : v}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-[11px] text-slate-400">
            Sources: vendor pricing pages and 2026 buyer guides. Caply does not resell market data and does not
            charge per data room.
          </p>
        </div>
      </Section>

      {/* ── security ────────────────────────────────────────────────────── */}
      <Section id="security" className="border-y border-slate-100 bg-slate-50/70">
        <div className="mx-auto max-w-6xl px-5 py-16">
          <div className="grid gap-10 md:grid-cols-[0.9fr_1.1fr]">
            <div>
              <Eyebrow>Security and data</Eyebrow>
              <h2 className="mt-2 text-3xl font-semibold tracking-tight text-navy-900">
                Your documents stay in your workspace
              </h2>
              <p className="mt-3 text-[15px] leading-relaxed text-slate-600">
                Caply runs on Microsoft Azure. Each firm gets its own isolated tenant, its own storage
                container, and its own sign-in boundary. Analysts never download a CIM to their laptop to
                make the model work.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {[
                { icon: Building2, t: 'Tenant isolation', c: 'Every query is scoped to your firm. A document cannot be reached from another workspace.' },
                { icon: Lock, t: 'Encrypted at rest', c: 'Documents and records live in Azure storage and Cosmos DB with encryption at rest.' },
                { icon: ShieldCheck, t: 'Microsoft Entra sign-in', c: 'Email and password or your own identity provider, with SSO on the Firm plan.' },
                { icon: FileText, t: 'Audit trail', c: 'Records carry who created and changed them, and phase gates require a written reason.' },
              ].map(x => (
                <div key={x.t} className="rounded-xl border border-slate-200 bg-white p-5">
                  <x.icon className="w-4 h-4 text-brand-600" />
                  <h3 className="mt-2.5 text-[13px] font-semibold text-navy-900">{x.t}</h3>
                  <p className="mt-1.5 text-[12.5px] leading-relaxed text-slate-500">{x.c}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* ── faq ─────────────────────────────────────────────────────────── */}
      <Section id="faq" className="mx-auto max-w-4xl px-5 py-16">
        <div className="text-center">
          <Eyebrow>Questions</Eyebrow>
          <h2 className="mt-2 text-3xl font-semibold tracking-tight text-navy-900">The ones partners ask</h2>
        </div>

        <div className="mt-8 divide-y divide-slate-200 rounded-xl border border-slate-200">
          {FAQ.map((f, i) => (
            <div key={f.q}>
              <button
                onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
              >
                <span className="text-[13.5px] font-semibold text-navy-900">{f.q}</span>
                <ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
              </button>
              {openFaq === i && (
                <p className="px-5 pb-4 text-[13px] leading-relaxed text-slate-600">{f.a}</p>
              )}
            </div>
          ))}
        </div>
      </Section>

      {/* ── closing cta ─────────────────────────────────────────────────── */}
      <Section className="bg-navy-950">
        <div className="mx-auto max-w-4xl px-5 py-16 text-center">
          <Mark className="mx-auto w-12 h-12" />
          <h2 className="mt-5 text-3xl font-semibold tracking-tight text-white">
            Put one live deal through it this week
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-[14.5px] leading-relaxed text-slate-300">
            Bring a CIM you already know well. If the screening memo and the IC pack do not beat
            what your team would have produced, you have lost an afternoon.
          </p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            <button onClick={start} className="inline-flex items-center gap-2 rounded-md bg-brand-500 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-brand-600">
              {startLabel}<ArrowRight className="w-4 h-4" />
            </button>
            <Link to="/login" className="inline-flex items-center gap-2 rounded-md border border-white/25 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10">
              Sign in
            </Link>
          </div>
        </div>
      </Section>

      {/* ── footer ──────────────────────────────────────────────────────── */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl flex-col gap-6 px-5 py-10 md:flex-row md:items-start md:justify-between">
          <div>
            <div className="flex items-center gap-2.5">
              <Mark className="w-8 h-8" />
              <span className="text-sm font-semibold text-navy-900">Caply</span>
            </div>
            <p className="mt-2 max-w-xs text-[12px] leading-relaxed text-slate-500">
              One workspace for private equity deal sourcing, diligence, modeling and portfolio monitoring.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-8 text-[12.5px] sm:grid-cols-3">
            <div className="space-y-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Product</p>
              <a href="#platform" className="block text-slate-600 hover:text-navy-900">Platform</a>
              <a href="#pricing" className="block text-slate-600 hover:text-navy-900">Pricing</a>
              <a href="#security" className="block text-slate-600 hover:text-navy-900">Security</a>
            </div>
            <div className="space-y-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Resources</p>
              <a href="/user-guide.html" className="block text-slate-600 hover:text-navy-900">User guide</a>
              <a href="/glossary.html" className="block text-slate-600 hover:text-navy-900">PE glossary</a>
              <a href="#faq" className="block text-slate-600 hover:text-navy-900">FAQ</a>
              <a href="/terms.html" className="block text-slate-600 hover:text-navy-900">Terms of Use</a>
              <a href="/privacy.html" className="block text-slate-600 hover:text-navy-900">Privacy Policy</a>
            </div>
            <div className="space-y-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Account</p>
              <Link to="/login" className="block text-slate-600 hover:text-navy-900">Sign in</Link>
              <button onClick={start} className="block text-left text-slate-600 hover:text-navy-900">Start a trial</button>
              <a href="mailto:support@caply4pe.com" className="block text-slate-600 hover:text-navy-900">support@caply4pe.com</a>
            </div>
          </div>
        </div>
        <div className="border-t border-slate-100">
          <div className="mx-auto flex max-w-6xl flex-col gap-2 px-5 py-5 text-[11px] text-slate-400 sm:flex-row sm:items-center sm:justify-between">
            <p>
              © 2026 Caply. All rights reserved. ·{' '}
              <a href="/terms.html" className="hover:text-navy-900">Terms of Use</a> ·{' '}
              <a href="/privacy.html" className="hover:text-navy-900">Privacy Policy</a>
            </p>
            <p>Built on Microsoft Azure. Caply is a product of its own operators, not affiliated with the vendors named above.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
